/**
 * Comments and the per-document chat (slate-feature-transfer W8 §A.1, §B.8, §B.9).
 *
 * Two families, one file, because they are the wave's two remaining namespaces and both are
 * enveloped where everything else in W8 is bare:
 *
 *  - **`comments.*`** is the ONLY `{data, policies}` family in the wave. Its `policies` block
 *    is not load-bearing: the server's `BuildPolicies` sets all 34 ability keys to `true` for
 *    every id, so it says nothing about what this viewer may do. Slate therefore gates on the
 *    HOST DOCUMENT's policy plus own-authorship, exactly as the rest of W8 does, and keeps the
 *    parsed policies only so a future server that means them is a one-line change.
 *  - **`documents.chat.*`** is `{data}`, and is the one properly gated pair in the whole wave
 *    (`IAccessResolver.HasDocumentMembershipAsync`). Its 403 has an EMPTY body.
 *
 * ⚠ W8-5 — `Comment.UpdateReactions(...)` is never called server-side, so the `reactions`
 * column on a presented comment is **always `[]`**. Reaction counts have never worked. The
 * summary a reader sees is folded here from `reactions.list` rows and is labelled derived.
 */

import { z } from "zod";

// ── Comments ─────────────────────────────────────────────────────────────────

/**
 * The comment body is ProseMirror JSON on the wire (`data`). Slate's composer is plain text —
 * a rich comment editor is a different wave — so the two helpers below are the whole
 * translation, and they are deliberately lossy in ONE direction only: reading keeps every
 * text node a richer client wrote, writing produces the simplest valid document.
 */
export type CommentDoc = Record<string, unknown>;

export function commentDoc(text: string): CommentDoc {
  const paragraphs = text.split(/\n{2,}/).filter((block) => block.trim().length > 0);
  return {
    type: "doc",
    content: (paragraphs.length > 0 ? paragraphs : [""]).map((block) => ({
      type: "paragraph",
      ...(block.length > 0 ? { content: [{ type: "text", text: block }] } : {}),
    })),
  };
}

/** Depth-first text extraction. An unknown node shape contributes nothing rather than throwing. */
export function commentText(data: unknown): string {
  const walk = (node: unknown): string => {
    if (typeof node === "string") {
      return node;
    }
    if (Array.isArray(node)) {
      return node.map(walk).join("");
    }
    if (!node || typeof node !== "object") {
      return "";
    }
    const record = node as Record<string, unknown>;
    if (record.type === "text" && typeof record.text === "string") {
      return record.text;
    }
    const inner = walk(record.content);
    // A paragraph (or any block) ends a line; inline nodes do not.
    return record.type === "paragraph" || record.type === "heading" ? `${inner}\n` : inner;
  };
  return walk(data).replace(/\n+$/, "");
}

const CommentUserSchema = z.object({
  id: z.string().default(""),
  name: z.string().default(""),
  avatarUrl: z.string().nullable().default(null),
});
export type CommentUser = z.infer<typeof CommentUserSchema>;

export const CommentSchema = z.object({
  id: z.string(),
  documentId: z.string().default(""),
  parentCommentId: z.string().nullable().default(null),
  createdById: z.string().default(""),
  createdBy: CommentUserSchema.nullable().default(null),
  resolvedById: z.string().nullable().default(null),
  resolvedBy: CommentUserSchema.nullable().default(null),
  resolvedAt: z.string().nullable().default(null),
  data: z.unknown().default(null),
  anchorText: z.string().nullable().default(null),
  createdAt: z.string().nullable().default(null),
  updatedAt: z.string().nullable().default(null),
});
export type Comment = z.infer<typeof CommentSchema>;

/** Folded client-side from `reactions.list`, because the server's cache column never fills. */
export interface ReactionSummary {
  emoji: string;
  userIds: string[];
}

export const ReactionRowSchema = z.object({
  id: z.string().default(""),
  emoji: z.string().default(""),
  commentId: z.string().default(""),
  userId: z.string().default(""),
  createdAt: z.string().nullable().default(null),
});
export type ReactionRow = z.infer<typeof ReactionRowSchema>;

/** One row per user+emoji becomes one chip per emoji, in first-seen order. */
export function foldReactions(rows: ReactionRow[]): ReactionSummary[] {
  const byEmoji = new Map<string, string[]>();
  for (const row of rows) {
    if (!row.emoji) {
      continue;
    }
    const users = byEmoji.get(row.emoji) ?? [];
    if (row.userId && !users.includes(row.userId)) {
      users.push(row.userId);
    }
    byEmoji.set(row.emoji, users);
  }
  return [...byEmoji.entries()].map(([emoji, userIds]) => ({ emoji, userIds }));
}

export interface CommentPage {
  comments: Comment[];
  total: number;
  offset: number;
  limit: number;
}

export interface CommentListQuery {
  documentId?: string;
  parentCommentId?: string;
  collectionId?: string;
  sort?: "createdAt" | "updatedAt";
  direction?: "ASC" | "DESC";
  /** `["resolved"]` / `["unresolved"]`. Both, or neither, means no filter (server behaviour). */
  statusFilter?: Array<"resolved" | "unresolved">;
  offset?: number;
  limit?: number;
}

export interface CommentDraft {
  documentId: string;
  /** Plain text; the adapter wraps it. Pass `data` instead to send a document you built. */
  text?: string;
  data?: CommentDoc;
  /**
   * ⚠ S10's identity fusion: for a THREAD ROOT this is left unset and the client mints the
   * thread id itself so the same id can also be the conversation id. A reply carries the root.
   */
  parentCommentId?: string;
}

/**
 * A thread as the UI needs it: the root, its replies in wire order, and whether it is resolved.
 * `Comment.resolve` throws server-side for a reply, so resolution is a property of the ROOT.
 */
export interface CommentThread {
  id: string;
  root: Comment;
  replies: Comment[];
  resolved: boolean;
}

/** Group a flat page into threads. Orphan replies keep their own thread rather than vanishing. */
export function toThreads(comments: Comment[]): CommentThread[] {
  const roots = comments.filter((comment) => !comment.parentCommentId);
  const known = new Set(roots.map((comment) => comment.id));
  const orphans = comments.filter(
    (comment) => comment.parentCommentId && !known.has(comment.parentCommentId),
  );
  return [...roots, ...orphans].map((root) => ({
    id: root.id,
    root,
    replies: comments.filter((comment) => comment.parentCommentId === root.id),
    resolved: root.resolvedAt !== null,
  }));
}

// ── Document chat (S9) ───────────────────────────────────────────────────────

/** The server trims and then silently truncates. Saying so in the UI beats losing the tail. */
export const DOCUMENT_CHAT_MAX_CHARS = 8000;
export const DOCUMENT_CHAT_AI_AUTHOR_ID = "ai";

export const DocumentChatMessageSchema = z.object({
  id: z.string(),
  documentId: z.string().default(""),
  authorId: z.string().default(""),
  authorName: z.string().default(""),
  authorAvatarUrl: z.string().nullable().default(null),
  /** Only two values here — this table has no `system` author. */
  authorType: z.string().default("human"),
  content: z.string().default(""),
  createdAt: z.string().nullable().default(null),
});
export type DocumentChatMessage = z.infer<typeof DocumentChatMessageSchema>;

// ── The AI transform inside a comment thread (S10) ───────────────────────────

/** `POST /api/ai-actions/transform-selection` answers 200 with text, or 202 with a dispatch. */
export interface SelectionTransformDraft {
  documentId?: string;
  collectionId?: string;
  documentTitle?: string;
  instructions: string;
  selectedText: string;
  thinkerId?: string;
}

/**
 * One transform reply as the thread renders it. It is a conversation MESSAGE
 * (`messageType:"transform"`), keyed to its comment by `metadata.parentCommentId` — there is
 * no parent field on the AI side and no join table, which is why the clean-room thread needs
 * no reply tree for AI rows at all.
 */
export interface TransformReply {
  messageId: string;
  parentCommentId: string;
  thinkerId: string | null;
  thinkerName: string | null;
  model: string | null;
  instructions: string | null;
  selectedText: string | null;
  updatedText: string | null;
  diff: string | null;
  createdAt: string | null;
}

const strOrNull = (value: unknown): string | null => (typeof value === "string" ? value : null);

/** Reads the metadata a transform message carries. Anything missing is `null`, never a throw. */
export function readTransformReply(
  messageId: string,
  createdAt: string | null,
  metadata: unknown,
): TransformReply | null {
  if (!metadata || typeof metadata !== "object") {
    return null;
  }
  const record = metadata as Record<string, unknown>;
  const parentCommentId = strOrNull(record.parentCommentId);
  if (!parentCommentId) {
    return null;
  }
  return {
    messageId,
    parentCommentId,
    thinkerId: strOrNull(record.thinkerId),
    thinkerName: strOrNull(record.thinkerName),
    model: strOrNull(record.model),
    instructions: strOrNull(record.instructions),
    selectedText: strOrNull(record.selectedText),
    updatedText: strOrNull(record.updatedText),
    diff: strOrNull(record.diff),
    createdAt,
  };
}

/**
 * The thinker selection a think thread remembers. kms persisted it as a `config` MESSAGE
 * because the server has no other table for it, and restored it by scanning messages
 * BACKWARDS for the last one — reproduced here as one typed accessor so no component ever
 * scans a message array itself (§F.4 decision 4).
 */
export interface ThinkerConfig {
  thinkerIds: string[];
  thinkerNames: string[];
}

export function readThinkerConfig(
  messages: Array<{ messageType: string; metadata: unknown }>,
): ThinkerConfig | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.messageType !== "config") {
      continue;
    }
    const record = (message.metadata ?? {}) as Record<string, unknown>;
    const ids = Array.isArray(record.thinkerIds)
      ? record.thinkerIds.filter((entry): entry is string => typeof entry === "string")
      : [];
    const names = Array.isArray(record.thinkerNames)
      ? record.thinkerNames.filter((entry): entry is string => typeof entry === "string")
      : [];
    return { thinkerIds: ids, thinkerNames: names };
  }
  return null;
}
